// =============================================================================
// ping-client.ts — Client API du ping S0, DOUBLABLE (Option B, GATE_HUMAN).
// =============================================================================
// « Option B confirmée : skeleton CI contre doubles ». Il n'y a PAS de backend
// réel en S0 : ce module expose une INTERFACE (PingClient) et une IMPLÉMENTATION
// stub déterministe. Le bearer/tenant proviennent d'un AuthContext simulé
// (src/auth/), jamais codés en dur dans l'UI métier.
//
// Le type d'événement est ANCRÉ au Published Language via un import de TYPE pur
// (`import type` => effacé à la compilation, aucun zod dans le bundle UI). Seul le
// Domain Event DeS0Ping fait partie du Published Language ; les DTO HTTP ci-dessous
// (S0PingRequest/Response) n'en font PAS partie et restent donc locaux jusqu'à ce
// que l'API expose un client typé partagé. Ce stub sera alors remplacé par l'impl
// HTTP réelle derrière la même interface PingClient.

import type { DeS0Ping } from '@jeko/contracts';

// Ancré au contrat : si DeS0Ping['type'] change côté @jeko/contracts, cette ligne
// cesse de compiler (garde-fou anti-dérive du Published Language).
export const S0_PING_EVENT_TYPE: DeS0Ping['type'] = 'DE.S0Ping';

/** Requête POST /s0/ping (miroir de la forme attendue par l'endpoint D4). */
export interface S0PingRequest {
  note?: string;
}

/** Réponse de l'endpoint (miroir de la projection renvoyée par D4). */
export interface S0PingResponse {
  pong: true;
  tenantId: string;
  note?: string;
  /** ISO8601. */
  at: string;
  correlationId: string;
}

/** Identité simulée transmise au client (jamais en dur dans l'UI). */
export interface PingAuth {
  bearer: string;
  tenantId: string;
}

/** Contrat du client ping. Une seule méthode pour la tranche verticale S0. */
export interface PingClient {
  ping(request: S0PingRequest, auth: PingAuth): Promise<S0PingResponse>;
}

/** UUID v4 via l'API Web Crypto (présente en navigateur et jsdom récent). */
function newCorrelationId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * Stub déterministe du client ping (Option B). Simule la réponse de l'API :
 * echo du tenant/note + corrélation, exactement comme le ferait l'endpoint D4
 * après écriture sous RLS. Aucun I/O réseau.
 */
export class StubPingClient implements PingClient {
  async ping(request: S0PingRequest, auth: PingAuth): Promise<S0PingResponse> {
    if (!auth.bearer) {
      throw new Error('Authentification requise : bearer manquant.');
    }
    // Spread conditionnel : sous `exactOptionalPropertyTypes`, la clé `note`
    // n'est présente que si une note a été fournie (jamais `note: undefined`).
    return {
      pong: true,
      tenantId: auth.tenantId,
      at: new Date().toISOString(),
      correlationId: newCorrelationId(),
      ...(request.note !== undefined ? { note: request.note } : {}),
    };
  }
}

/** Instance partagée par défaut (injectable/override pour les tests). */
export const defaultPingClient: PingClient = new StubPingClient();
